import { UploadConfig, UploadError, UploadErrorCode, UploadFormat, ValidationFailure, ValidationResult, ValidationSuccess } from "./types";
import { getUploadMessages } from "../locales/messages";

const messages = getUploadMessages();

export interface ValidateFilesOptions {
  config: UploadConfig;
  /** 已经上传/已选择的数量 */
  uploadedCount?: number;
}

export interface FileValidationResult {
  file: File;
  error: UploadError;
}

export interface ValidateFilesResponse {
  validFiles: File[];
  invalidFiles: FileValidationResult[];
  countError?: UploadError;
}

/**
 * 对本次选择的所有文件做「前置校验」。
 *
 * - 只负责本地验证（数量 / 大小 / 格式），不做任何网络请求
 * - 所有失败的文件都会返回在 invalidFiles 中，并带上 UploadError（包含 error.code）
 * - 调用方（useUpload）需要为 invalidFiles 创建 UploadItem（status: "error"），
 *   这样即使没有进入上传阶段，这些文件也会固定展示在 UploadProgressPanel 中
 */
export async function validateFiles(
  files: File[],
  options: ValidateFilesOptions,
): Promise<ValidateFilesResponse> {
  const { config, uploadedCount = 0 } = options;
  const limit = Math.min(config.maxFiles, (config.maxTotal ?? Infinity) - uploadedCount);
  const remaining = Math.max(limit, 0);

  const filesWithinLimit = files.slice(0, remaining);
  const overflow = files.length - filesWithinLimit.length;
  const invalidFiles: FileValidationResult[] = [];

  if (remaining <= 0) {
    return {
      validFiles: [],
      invalidFiles: files.map((file) => ({
        file,
        error: createCountExceededError(config, 0),
      })),
      countError: createCountExceededError(config, 0),
    };
  }

  let countError: UploadError | undefined;
  if (overflow > 0) {
    countError = createCountExceededError(config, remaining);
  }

  // 并发执行验证以提高性能
  const validatorResults = await Promise.all(
    filesWithinLimit.map(async (file) => {
      // 先进行快速的大小检查
      const sizeValidation = validateFileSize(file, config.maxFileSize);
      if (!sizeValidation.valid) {
        return sizeValidation;
      }

      // 然后进行格式验证（可能需要读取文件头）
      const formatValidation = await validateFileFormat(file, config.formats);
      if (!formatValidation.valid) {
        return formatValidation;
      }

      return { valid: true } as ValidationSuccess;
    }),
  );

  const validFiles: File[] = [];
  validatorResults.forEach((result, index) => {
    const file = filesWithinLimit[index];
    if (result.valid) {
      validFiles.push(file);
    } else {
      invalidFiles.push({ file, error: result.error });
    }
  });

  if (countError) {
    return {
      validFiles,
      invalidFiles,
      countError,
    };
  }

  return {
    validFiles,
    invalidFiles,
  };
}

export function validateFileSize(file: File, maxSizeMB: number): ValidationResult {
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > maxSizeMB) {
    return createFailure(
      UploadErrorCode.SIZE_EXCEEDED,
      messages.errors.sizeExceeded(maxSizeMB),
    );
  }

  return createSuccess();
}

/**
 * 文件格式校验：
 * - 通过扩展名 + 可选的自定义 validator 判定是否允许上传
 * - 对于非 PDF / 不被允许的格式，统一返回 UploadErrorCode.FORMAT_INVALID
 * - 这些错误不会进入 OSS 上传流程，但会以「错误任务」的形式展示在面板中
 */
export async function validateFileFormat(file: File, formats: UploadFormat[]): Promise<ValidationResult> {
  const lowerCaseName = file.name.toLowerCase();
  const matchedFormat = formats.find(({ extensions }) =>
    extensions.some((ext) => lowerCaseName.endsWith(ext.toLowerCase())),
  );

  if (!matchedFormat) {
    // 非 PDF 文件需要在上传面板中以错误任务形式展示，并且不进入 OSS 上传流程
    return createFailure(UploadErrorCode.FORMAT_INVALID, messages.errors.formatInvalid);
  }

  if (matchedFormat.validator) {
    const valid = await matchedFormat.validator(file);
    if (!valid) {
      // 与上面相同，保持进入队列但跳过上传
      return createFailure(UploadErrorCode.FORMAT_INVALID, messages.errors.formatInvalid);
    }
  }

  return createSuccess();
}

/**
 * 选择文件「之前」的数量校验（用于在组件外做预判，比如弹出二次确认对话框）。
 *
 * PaperUpload 当前的数量控制主要在 validateFiles 内部完成，
 * 但如果业务需要在真正读取文件前就判断「本次选择是否会超出总上限」，
 * 可以在外层调用 validateCountBeforeUpload 来给出更早的交互提示。
 */
export function validateCountBeforeUpload(
  nextSelectionCount: number,
  config: UploadConfig,
  uploadedCount: number,
): ValidationResult {
  const total = uploadedCount + nextSelectionCount;
  const limit = config.maxTotal ?? config.maxFiles;
  if (total > limit) {
    const remaining = Math.max(limit - uploadedCount, 0);
    return createFailure(
      UploadErrorCode.COUNT_EXCEEDED,
      messages.errors.countExceeded(limit, remaining),
    );
  }

  if (nextSelectionCount > config.maxFiles) {
    return createFailure(
      UploadErrorCode.COUNT_EXCEEDED,
      messages.errors.singleCountExceeded(config.maxFiles),
    );
  }

  return createSuccess();
}

/**
 * 构造「总数量超限」的错误对象。
 * - code: COUNT_EXCEEDED
 * - message: 带出总上限与本次仍可选择的数量
 */
function createCountExceededError(config: UploadConfig, remaining: number): UploadError {
  const limit = config.maxTotal ?? config.maxFiles;
  return {
    code: UploadErrorCode.COUNT_EXCEEDED,
    message: messages.errors.countExceeded(limit, remaining),
  };
}

function createFailure(code: UploadErrorCode, message: string): ValidationFailure {
  return {
    valid: false,
    error: {
      code,
      message,
    },
  };
}

function createSuccess(): ValidationSuccess {
  return { valid: true };
}
